import { useEffect } from 'react';
import { IMPERSONATION_INACTIVITY_CHECK_INTERVAL_MS } from '@/constants';
import { setImpersonationRequestActivityHandler } from '@/services/api';
import { impersonationService } from '@/services/impersonation.service';
import { impersonationSessionService } from '@/services/impersonationSession.service';
import { useImpersonation } from './useImpersonation';

export const useImpersonationInactivityTimeout = (): void => {
  const { isImpersonating } = useImpersonation();

  useEffect(() => {
    if (!isImpersonating) {
      setImpersonationRequestActivityHandler(null);
      return;
    }

    const handleRequestActivity = (): void => {
      impersonationService.recordActivity();
    };

    const checkInactivity = (): void => {
      if (impersonationService.isInactivityTimeoutReached()) {
        void impersonationSessionService.endSession();
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      // The interval is throttled while the tab is hidden, so re-check on return
      // to catch sessions that crossed the inactivity threshold in the background.
      checkInactivity();
    };

    // End immediately when the threshold was already crossed while unmounted
    // (e.g. a reload or device sleep that paused the interval).
    if (impersonationService.isInactivityTimeoutReached()) {
      void impersonationSessionService.endSession();
      return;
    }

    setImpersonationRequestActivityHandler(handleRequestActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(checkInactivity, IMPERSONATION_INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      setImpersonationRequestActivityHandler(null);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [isImpersonating]);
};
